import { lineQty, revenueId, saleMethods } from './kassa'
import { money } from '../format/currency'
import type { RevenueOrder } from './revenue'
import type { KuurRefundMethod, Sale, SaleReturn, SaleReturnLine, SaleReturnLineInput } from '../types/kassa'

/**
 * Retour op een kassabon (docs/KASSA-ABONNEMENTEN.md §3, "Retour en creditnota"). Puur en getest;
 * de server rekent hetzelfde.
 *
 * - Corrigeren (de hele bon ongedaan) kan alleen op de dag zelf vóór de afsluiting. Daarna, of voor
 *   een deel van de bon: een retour met een eigen creditnota, op de dag van vandaag.
 * - Terug kunnen producten (per stuk) en betaalde behandelingen (bijv. bij een klacht). Een kuur gaat
 *   via Kuur stoppen, een verkochte cadeaubon via Cadeaubonnen, en "uit kuur" (€ 0) heeft niets terug te geven.
 * - Bedrag = wat de klant per stuk betaalde (na korting) × stuks. Met pin of contant hooguit wat er
 *   met pin of contant binnenkwam; wat met cadeaubon of tegoed betaald is, gaat terug als tegoed.
 */

/** Hoeveel stuks van regel `lineIndex` nog terug kunnen. */
export function returnableQty(sale: Pick<Sale, 'lines' | 'returns'>, lineIndex: number): number {
  const line = sale.lines[lineIndex]
  if (!line || line.kind === 'kuur' || line.kind === 'giftcard' || line.totalCents <= 0) return 0
  const returned = (sale.returns ?? []).reduce((t, r) => t + r.lines.filter((l) => l.lineIndex === lineIndex).reduce((s, l) => s + l.qty, 0), 0)
  return Math.max(0, lineQty(line) - returned)
}

/** Kan er nog iets terug van deze bon? */
export const canReturn = (sale: Sale) => sale.status === 'betaald' && sale.lines.some((_, i) => returnableQty(sale, i) > 0)

/** Wat er nog met pin of contant terug kan (samen): wat zo binnenkwam, min eerdere retouren met pin of contant. */
export const moneyRefundableCents = (sale: Pick<Sale, 'methodCents' | 'returns'>) =>
  Math.max(0, sale.methodCents - (sale.returns ?? []).reduce((t, r) => t + (r.method === 'tegoed' ? 0 : r.refundCents), 0))

type RefundSource = Pick<Sale, 'method' | 'methodCents' | 'cashPartCents' | 'returns'>

/**
 * Per manier wat er nog terug kan: hooguit wat er via die manier binnenkwam, min eerdere retouren
 * via die manier. Bij een gesplitste bon (€ 80 pin + € 20 contant) dus nooit € 100 via pin: dan
 * klopt de pinautomaat of de la niet meer met de dag.
 */
export function refundableByMethod(sale: RefundSource): { pin: number; contant: number } {
  const received = saleMethods(sale)
  const back = (m: 'pin' | 'contant') => (sale.returns ?? []).reduce((t, r) => t + (r.method === m ? r.refundCents : 0), 0)
  return { pin: Math.max(0, received.pin - back('pin')), contant: Math.max(0, received.contant - back('contant')) }
}

/** De gekozen regels zoals op de creditnota (stuks 0 vallen weg). */
export function returnLines(sale: Pick<Sale, 'lines'>, inputs: readonly SaleReturnLineInput[]): SaleReturnLine[] {
  return inputs.flatMap((input) => {
    const line = sale.lines[input.lineIndex]
    if (!line || input.qty <= 0) return []
    return [
      {
        lineIndex: input.lineIndex,
        kind: line.kind,
        name: line.name,
        revenueId: revenueId(line),
        qty: input.qty,
        unitCents: line.priceCents,
        vatRate: line.vatRate,
        totalCents: line.priceCents * input.qty,
      },
    ]
  })
}

export const returnTotalCents = (lines: readonly Pick<SaleReturnLine, 'totalCents'>[]) => lines.reduce((t, l) => t + l.totalCents, 0)

export type ReturnInput = { lines: readonly SaleReturnLineInput[]; method: KuurRefundMethod; reason: string }

/** Wat er niet klopt aan een retour, met het veld; `null` = in orde. */
export function returnProblem(sale: Sale, input: ReturnInput): { message: string; field: 'lines' | 'method' | 'reason' } | null {
  if (sale.status !== 'betaald') return { message: 'Deze bon is gecorrigeerd; er valt niets meer terug te nemen.', field: 'lines' }
  const chosen = input.lines.filter((l) => l.qty !== 0)
  if (chosen.length === 0) return { message: 'Kies wat er terugkomt.', field: 'lines' }
  const seen = new Set<number>()
  for (const l of chosen) {
    const line = sale.lines[l.lineIndex]
    if (!line || seen.has(l.lineIndex)) return { message: 'Deze regel staat niet (meer) op de bon.', field: 'lines' }
    seen.add(l.lineIndex)
    if (line.kind === 'kuur') return { message: 'Een kuur gaat terug via het dossier: Kuur stoppen en terugbetalen.', field: 'lines' }
    if (line.kind === 'giftcard') return { message: 'Een verkochte cadeaubon pas je aan bij Cadeaubonnen.', field: 'lines' }
    const max = returnableQty(sale, l.lineIndex)
    if (!Number.isInteger(l.qty) || l.qty < 1 || l.qty > max) {
      return { message: max === 0 ? `${line.name} is al helemaal terug.` : `${line.name}: hooguit ${max} terug.`, field: 'lines' }
    }
  }
  if (input.method !== 'pin' && input.method !== 'contant' && input.method !== 'tegoed') return { message: 'Kies hoe het geld teruggaat.', field: 'method' }
  if (input.method === 'tegoed' && !sale.customerId) return { message: 'Als tegoed kan alleen bij een klant met account.', field: 'method' }
  const total = returnTotalCents(returnLines(sale, chosen))
  if (input.method !== 'tegoed' && total > moneyRefundableCents(sale)) {
    const max = moneyRefundableCents(sale)
    return {
      message: sale.customerId
        ? `Deze bon is deels met cadeaubon of tegoed betaald: hooguit ${money(max)} kan via pin of contant terug. Kies Als tegoed.`
        : `Deze bon is deels met een cadeaubon betaald: hooguit ${money(max)} kan via pin of contant terug. Neem minder terug, of maak een nieuwe cadeaubon voor de rest.`,
      field: 'method',
    }
  }
  if (input.method !== 'tegoed') {
    const left = refundableByMethod(sale)[input.method]
    if (total > left) {
      const other = input.method === 'pin' ? 'contant' : 'pin'
      return {
        message: `Via ${input.method} kwam ${left === 0 ? 'niets (meer)' : `hooguit ${money(left)}`} binnen op deze bon. Kies ${other}, of doe het in twee retouren.`,
        field: 'method',
      }
    }
  }
  if (!input.reason.trim()) return { message: 'Geef een reden (komt op de creditnota).', field: 'reason' }
  return null
}

/**
 * Een retour als omzet: alleen de terugbetaling, op de dag van de retour (de verkoop telde al op de
 * dag van de bon). Producten tellen als teruggekomen stuks. Als tegoed: pin netto 0, tegoed omhoog
 * (zoals bij een gestopte kuur). `null` bij € 0.
 */
export function saleReturnRevenue(r: SaleReturn, performerId?: string): RevenueOrder | null {
  if (r.refundCents <= 0) return null
  return {
    paidAt: r.at,
    channel: 'salon',
    paymentMethod: r.method === 'tegoed' ? 'pin' : r.method,
    refundOnly: true,
    lines: r.lines.map((l) => ({
      productId: l.revenueId,
      name: l.name,
      qty: l.qty,
      unitPriceCents: l.unitCents,
      vatRate: l.vatRate,
      giftCard: false,
      treatment: l.kind === 'treatment',
    })),
    shippingCents: 0,
    shippingVatRate: 0,
    refunds: [{ at: r.at, amountCents: r.refundCents, lines: r.lines.map((l) => ({ productId: l.revenueId, qty: l.qty })) }],
    ...(r.method === 'tegoed' ? { creditCents: -r.refundCents } : {}),
    ...(performerId ? { performerId } : {}),
  }
}
